import { useState, useEffect, useRef, useCallback } from "react";
import { T } from "./theme";
import { X, Search, Building2, Download, Award } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import PageLoading from "./components/PageLoading";
import Card from "./components/Card";
import Button from "./components/Button";

const isMe = n => window.usuarioUnidade ? n?.toLowerCase().includes(window.usuarioUnidade.toLowerCase().split(' ')[0]) : n?.toLowerCase().includes("vicente");

const CORES = [T.accent, T.green, T.amber, T.purple];

function NpsBadge({ nota, mediaRJ }) {
  if (!mediaRJ || !nota) return null;
  const acima = nota >= mediaRJ;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
      background: acima ? `${T.green}20` : `${T.red}20`,
      color: acima ? T.green : T.red,
    }}>
      {acima ? "↑" : "↓"} RJ
    </span>
  );
}

export default function Benchmarking() {
  const [disponiveis, setDisponiveis]   = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [query, setQuery]               = useState("");
  const [dropdown, setDropdown]         = useState([]);
  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const debounce = useRef(null);

  useEffect(() => {
    fetch("/api/hospitais-disponiveis", { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error(`Erro ${r.status}`); return r.json(); })
      .then(setDisponiveis)
      .catch(e => setError(e.message));
  }, []);

  const onQueryChange = useCallback(e => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounce.current);
    if (v.length < 3) { setDropdown([]); return; }
    debounce.current = setTimeout(() => {
      const q = v.toLowerCase();
      setDropdown(
        disponiveis
          .filter(h => h.toLowerCase().includes(q) && !selecionados.includes(h) && !isMe(h))
          .slice(0, 8)
      );
    }, 300);
  }, [disponiveis, selecionados]);

  const addHospital = h => {
    if (selecionados.length >= 3) return;
    setSelecionados(s => [...s, h]);
    setQuery(""); setDropdown([]);
  };
  const removeHospital = h => setSelecionados(s => s.filter(x => x !== h));

  useEffect(() => {
    setLoading(true); setError(null);
    const qs = selecionados.length ? `?hospitais=${encodeURIComponent(selecionados.join(","))}` : "";
    fetch(`/api/benchmarking${qs}`, { credentials: "include" })
      .then(r => { if (!r.ok) throw new Error(`Erro ${r.status}`); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selecionados]);

  const areas      = data?.areas || [];
  const hospitais  = data?.hospitais || {};
  const mediaRJMap = data?.mediaRJ || {};
  const hospsOrdenados = Object.keys(hospitais).sort((a, b) => isMe(a) ? -1 : isMe(b) ? 1 : 0);

  const chartData = areas.map(area => {
    const row = { area: area.length > 14 ? area.slice(0, 13) + "…" : area };
    for (const h of hospsOrdenados) row[h] = hospitais[h]?.[area]?.nps ?? hospitais[h]?.[area]?.nota ?? null;
    if (mediaRJMap[area]) row["Media RJ"] = mediaRJMap[area];
    return row;
  }).filter(r => hospsOrdenados.some(h => r[h] !== null));

  const radarData = areas.filter(a => hospsOrdenados.some(h => hospitais[h]?.[a])).map(area => {
    // Abreviar nomes longos mas mantendo únicos
    const abrev = area
      .replace("Pronto Atendimento", "PA")
      .replace("Obstétrico", "Obst.")
      .replace("Infantil", "Inf.")
      .replace("Adulto", "Ad.")
      .replace("Centro Médico", "C. Médico")
      .replace("Internação", "Intern.")
      .replace("Maternidade", "Matern.")
      .replace("Teleconsulta", "Telecons.");
    const row = { area: abrev };
    hospsOrdenados.forEach(h => { row[h] = hospitais[h]?.[area]?.nps ?? hospitais[h]?.[area]?.nota ?? 0; });
    return row;
  });

  const meuHosp = hospsOrdenados.find(h => isMe(h));
  let minhaPosicao = null;
  if (meuHosp && hospsOrdenados.length > 1) {
    const meNPS = areas.reduce((s, a) => {
      const v = hospitais[meuHosp]?.[a]?.nota; return v ? s + v : s;
    }, 0) / (areas.filter(a => hospitais[meuHosp]?.[a]).length || 1);
    const hospsWithNPS = hospsOrdenados.map(h => ({
      h,
      nps: areas.reduce((s, a) => { const v = hospitais[h]?.[a]?.nota; return v ? s + v : s; }, 0) /
           (areas.filter(a => hospitais[h]?.[a]).length || 1),
    }));
    const rank = hospsWithNPS.filter(x => x.nps > meNPS).length + 1;
    minhaPosicao = { rank, total: hospsOrdenados.length };
  }

  const exportCSV = () => {
    const headers = ["Area", ...hospsOrdenados, "Media RJ"];
    const rows = areas.filter(a => hospsOrdenados.some(h => hospitais[h]?.[a])).map(a => [
      a,
      ...hospsOrdenados.map(h => hospitais[h]?.[a]?.nota?.toFixed(1) ?? ""),
      mediaRJMap[a]?.toFixed(1) ?? "",
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "benchmarking-truenps.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const tooltipStyle = { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, fontSize: 12, boxShadow: T.shadowMd };

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader icon={Award} title="Benchmarking" description="Comparativo entre hospitais">
        {hospsOrdenados.length > 0 && (
          <Button variant="secondary" size="sm" icon={Download} onClick={exportCSV}>
            Exportar CSV
          </Button>
        )}
      </PageHeader>

      {/* Seletor de hospitais */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.textMid, marginBottom: 12, fontFamily: T.fontBody }}>
          Selecionar hospitais para comparar <span style={{ color: T.textLow, fontWeight: 400 }}>(max. 3)</span>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: selecionados.length ? 12 : 0 }}>
          {selecionados.map((h, i) => (
            <span key={h} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "5px 10px", borderRadius: T.radiusFull, fontSize: 12,
              background: `${CORES[i + 1] || T.accent}20`,
              border: `1px solid ${CORES[i + 1] || T.accent}50`,
              color: CORES[i + 1] || T.accent,
            }}>
              {h}
              <button onClick={() => removeHospital(h)} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit", padding: 0, display: "flex" }}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>

        {selecionados.length < 3 && (
          <div style={{ position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.textLow }} />
            <input
              value={query} onChange={onQueryChange}
              placeholder="Digite 3+ letras para buscar hospital..."
              style={{ width: "100%", padding: T.inputPaddingWithIcon, background: T.bgInput, border: "1px solid transparent", borderRadius: T.inputRadius, color: T.textHi, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: T.fontBody, transition: T.transitionFast }}
            />
            {dropdown.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, zIndex: 50, marginTop: 4, boxShadow: T.shadowMd }}>
                {dropdown.map(h => (
                  <div key={h} onClick={() => addHospital(h)} style={{ padding: "9px 14px", fontSize: 13, cursor: "pointer", color: T.textMid, borderBottom: `1px solid ${T.border}`, transition: T.transitionFast }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <Building2 size={12} style={{ marginRight: 6, color: T.textLow }} />
                    {h}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {error && !loading && (
        <div style={{ padding: 20, background: `${T.red}10`, border: `1px solid ${T.red}30`, borderRadius: T.radius, color: T.red, fontSize: 13, marginBottom: 16 }}>
          Erro ao carregar benchmarking: {error}
        </div>
      )}

      {loading && <PageLoading label="Carregando..." />}

      {!loading && data && hospsOrdenados.length > 0 && (
        <>
          {chartData.length > 0 && (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.textMid, marginBottom: 16, fontFamily: T.fontBody }}>NPS por Area</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                  <XAxis dataKey="area" tick={{ fill: T.textLow, fontSize: 10 }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fill: T.textLow, fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11, color: T.textLow }} />
                  {hospsOrdenados.map((h, i) => (
                    <Bar key={h} dataKey={h} fill={CORES[i]} radius={[3, 3, 0, 0]} maxBarSize={28} />
                  ))}
                  <ReferenceLine y={0} stroke={T.border} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {minhaPosicao && (
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              padding: "12px 20px", borderRadius: T.radiusSm, marginBottom: 16,
              background: `${T.accent}10`, border: `1px solid ${T.accent}20`,
            }}>
              <Award size={18} color={T.accent}/>
              <div>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.accent }}>Sua posicao: #{minhaPosicao.rank}</span>
                <span style={{ fontSize: 13, color: T.textLow }}> de {minhaPosicao.total} hospitais comparados</span>
              </div>
            </div>
          )}

          {radarData.length > 0 && hospsOrdenados.length > 0 && (
            <Card style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.textMid, marginBottom: 16, fontFamily: T.fontBody }}>Perfil por Area (Radar)</div>
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData} margin={{ top: 8, right: 20, bottom: 8, left: 20 }}>
                  <PolarGrid stroke={T.border}/>
                  <PolarAngleAxis dataKey="area" tick={{ fill: T.textLow, fontSize: 10 }}/>
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fill: T.textLow, fontSize: 9 }} axisLine={false}/>
                  {hospsOrdenados.map((h, i) => (
                    <Radar key={h} name={isMe(h) ? window.usuarioUnidade || "Sua Instituicao" : h} dataKey={h}
                      stroke={CORES[i]} fill={CORES[i]} fillOpacity={0.08} strokeWidth={2}/>
                  ))}
                  <Legend wrapperStyle={{ fontSize: 11, color: T.textLow }}/>
                  <Tooltip contentStyle={tooltipStyle}/>
                </RadarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Tabela comparativa */}
          <Card noPadding>
            <div style={{
              display: "grid",
              gridTemplateColumns: `180px repeat(${hospsOrdenados.length}, 1fr) 100px`,
              padding: T.tableHeaderPadding, borderBottom: `1px solid ${T.border}`,
              fontSize: T.tableHeaderFontSize, fontWeight: T.tableHeaderWeight, color: T.textLow, textTransform: "uppercase", letterSpacing: "0.06em",
              background: T.bgBase,
            }}>
              <span>Area</span>
              {hospsOrdenados.map((h, i) => (
                <span key={h} style={{ color: CORES[i], textAlign: "right" }}>
                  {isMe(h) ? "★ O SEU" : h.length > 16 ? h.slice(0, 15) + "…" : h}
                </span>
              ))}
              <span style={{ textAlign: "right" }}>Media RJ</span>
            </div>

            {areas.filter(area => hospsOrdenados.some(h => hospitais[h]?.[area])).map(area => (
              <div key={area} style={{
                display: "grid",
                gridTemplateColumns: `180px repeat(${hospsOrdenados.length}, 1fr) 100px`,
                padding: T.tableCellPadding, borderBottom: `1px solid ${T.border}`, alignItems: "center",
                transition: T.transitionFast,
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.bgBase}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <span style={{ fontSize: 12, color: T.textMid }}>{area}</span>
                {hospsOrdenados.map((h, i) => {
                  const d = hospitais[h]?.[area];
                  const eu = isMe(h);
                  return (
                    <div key={h} style={{ textAlign: "right" }}>
                      {d ? (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 700, color: eu ? T.amber : CORES[i] }}>
                            {d.nota.toFixed(1)}
                          </div>
                          {d.nps != null && (
                            <div style={{ fontSize: 10, fontWeight: 600, color: d.nps >= 75 ? T.green : d.nps >= 50 ? T.accent : d.nps >= 30 ? T.amber : T.red }}>
                              NPS {d.nps.toFixed(0)}
                            </div>
                          )}
                          <div style={{ fontSize: 10, color: d.variacao >= 0 ? T.green : T.red }}>
                            {d.variacao >= 0 ? "+" : ""}{d.variacao.toFixed(1)}
                          </div>
                        </>
                      ) : <span style={{ color: T.textLow, fontSize: 11 }}>—</span>}
                    </div>
                  );
                })}
                <div style={{ textAlign: "right" }}>
                  {mediaRJMap[area]
                    ? <span style={{ fontSize: 12, color: T.textLow }}>{mediaRJMap[area].toFixed(1)}</span>
                    : <span style={{ color: T.textLow, fontSize: 11 }}>—</span>}
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      {!loading && data && hospsOrdenados.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: T.textLow, fontSize: 13 }}>
          Busque e selecione hospitais para comparar com a sua Unidade.
        </div>
      )}
    </PageContainer>
  );
}
